<script>
import { storeToRefs } from 'pinia'
import { formButton, formMoney, greatherThan } from 'tb-design_system-web'
import { useForm } from 'vee-validate'
import { onMounted, ref, provide } from 'vue'
import { useMeta } from 'vue-meta'
import { useRoute, useRouter } from 'vue-router'
import * as yup from 'yup'

import formBusinessPartners from '@/components/shared/formBusinessPartners.vue'
import RegisterLayout from '@/components/shared/registerLayout.vue'
import StepProgressBar from '@/components/shared/stepProgressBar.vue'
import { moneyFormat } from '@/composables/utils/moneyConfig'
import { isValidIntlPhone } from '@/composables/utils/phoneGoogle'
import { getBanks, fetchCachedFinishData } from '@/services/api'
import { handleGlobalError } from '@/services/errorHandler'
import { useCache } from '@/stores/cache'
import { useOnboardingStore } from '@/stores/onboarding'

export default {
  name: 'RegisterFinishPJStep1',
  components: {
    RegisterLayout,
    StepProgressBar,
    formBusinessPartners,
    formMoney,
    formButton
  },
  setup() {
    useMeta({ title: 'Cadastro PJ - Etapa 1/3' })

    const route = useRoute()
    const router = useRouter()

    const onboardingStore = useOnboardingStore()
    const { pjForm } = storeToRefs(onboardingStore)
    const cacheStore = useCache()

    const loading = ref(false)

    const businessPartnersValue = ref([
      {
        name: '',
        phone: '',
        email: '',
        percentage: '',
        canSignContracts: false,
        avgMonthlyIncome: ''
      }
    ])

    const capitalSocialValue = ref('')

    const alreadySubmittedOnce = ref(false)
    provide('alreadySubmittedOnce', alreadySubmittedOnce)

    const { handleSubmit, errors } = useForm({
      validationSchema: yup.object({
        partners: yup
          .array()
          .of(
            yup.object().shape({
              name: yup.string().required('Nome é obrigatório'),
              email: yup.string().required('E-mail é obrigatório').email('E-mail inválido'),
              phone: yup
                .string()
                .required('Telefone é obrigatório')
                .test('is-valid-phone', 'Telefone inválido', (value) => isValidIntlPhone(value)),
              percentage: yup
                .number()
                .typeError('Participação deve ser um número')
                .required('Participação é obrigatória')
                .min(0, 'Mínimo 0%')
                .max(100, 'Máximo 100%'),
              canSignContracts: yup.boolean().default(false),
              avgMonthlyIncome: yup
                .number()
                .typeError('Renda média mensal deve ser um número')
                .nullable()
                .when('canSignContracts', {
                  is: true,
                  then: (schema) =>
                    schema
                      .required('Renda média mensal é obrigatória')
                      .min(1, 'Renda média mensal deve ser maior que 0')
                })
            })
          )
          .test(
            'sum-100-percent',
            'A soma das participações deve ser exatamente 100%',
            (partners) => {
              if (!Array.isArray(partners)) return false
              const total = partners.reduce((sum, partner) => {
                const percent = parseFloat(partner.percentage)
                return sum + (isNaN(percent) ? 0 : percent)
              }, 0)
              return Math.round(total) === 100
            }
          )
          .test(
            'at-least-one-signer',
            'Pelo menos um sócio precisa assinar pela empresa',
            (partners) => {
              if (!Array.isArray(partners)) return false
              return partners.some(
                (p) => p?.canSignContracts === true || p?.canSignContracts === 'true'
              )
            }
          ),

        capitalSocial: greatherThan(yup)
      })
    })

    onMounted(async () => {
      const token = route.query.token
      if (token == null) router.replace('/')

      onboardingStore.setToken(token)
      onboardingStore.setTypeClient('PJ')

      try {
        const { data: response } = await fetchCachedFinishData(token)
        cacheStore.setFinishCache(response.payload)

        const pjData = response.payload || {}
        onboardingStore.documentId = pjData.documentId
        onboardingStore.clientId = pjData.clientId
        onboardingStore.clientName = pjData.clientName
        onboardingStore.setPJForm(pjData)

        const fieldMap = {
          businessPartnersValue: 'businessPartners',
          capitalSocialValue: 'capitalSocial'
        }

        Object.entries(fieldMap).forEach(([refName, storeKey]) => {
          const valueFromStore = pjForm.value[storeKey]

          if (
            valueFromStore !== undefined &&
            !(Array.isArray(valueFromStore) && valueFromStore.length === 0)
          ) {
            eval(refName).value = valueFromStore
          }
        })
      } catch (err) {
        router.push('/')
        handleGlobalError(err, 'Erro ao buscar dados')
      }
      fetchBanks()
    })

    const fetchBanks = async () => {
      try {
        const response = await getBanks()
        const formattedBanks = response.data.payload.map((item) => ({
          key: item.id,
          name: `${item.id} - ${item.name}`
        }))
        cacheStore.setBankCache(formattedBanks)
      } catch (err) {
        handleGlobalError(err)
      }
    }

    const onSubmitAction = () => {
      alreadySubmittedOnce.value = true
      return onSubmit()
    }

    const onSubmit = handleSubmit(async () => {
      loading.value = true

      try {
        onboardingStore.setStepData(1, {
          businessPartners: businessPartnersValue.value,
          capitalSocial: capitalSocialValue.value
        })
        router.push('/finalizar-cadastro/bancos-pj')
      } catch (err) {
        handleGlobalError(err)
      } finally {
        loading.value = false
      }
    })

    return {
      loading,
      onSubmitAction,

      moneyFormat,

      businessPartnersValue,
      capitalSocialValue,

      alreadySubmittedOnce,
      errors
    }
  }
}
</script>

<template>
  <RegisterLayout>
    <div class="col-12 col-md-9 h-100 onboarding pb-5">
      <div class="row">
        <StepProgressBar class="mt-5 mb-5" :steps="['Sócios', 'Contas', 'Anexos']" :current="1" />
      </div>
      <div class="row d-flex justify-content-center mt-4">
        <div class="col-12 col-md-6 col-lg-5 d-flex flex-column align-items-center">
          <p class="text-center">Informações dos Sócios</p>

          <form-business-partners
            v-model="businessPartnersValue"
            :error="alreadySubmittedOnce ? errors.partners : undefined"
          />

          <form-money
            className="col-12 form-input"
            v-model="capitalSocialValue"
            label="Capital Social Integralizado"
            id="capitalSocial"
            name="capitalSocial"
            :config="moneyFormat('R$ ', '.', ',', 2)"
          />

          <form-button
            class="col-12 form-input"
            title="Próximo"
            :loading="loading"
            :isDisabled="loading"
            @clicked="onSubmitAction"
          />
        </div>
      </div>
    </div>
  </RegisterLayout>
</template>

<style lang="scss" scoped></style>
